import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, Input, OnChanges, OnDestroy, Optional, signal, SimpleChanges, WritableSignal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { enter } from 'src/app/animations/common.animations';
import { ExpanderGroupService } from 'src/app/components/expander/expander-group.service';
import { ExpanderComponent } from 'src/app/components/expander/expander.component';
import { HeadingComponent } from 'src/app/components/heading/heading.component';
import { ItemLabelComponent } from 'src/app/components/item-label/item-label.component';
import { TextInputControl } from 'src/app/form-controls/text-input/text-input.control';
import { ClinicalNoteFormService } from 'src/app/pages/patient/components/clinical-notes-panel/clinical-note-validity.service';
import { ChangedWidgetValue, DocumentInstanceBuildService, DocumentViewModel } from 'src/app/services/document-instance-build.service';
import { WidgetMetadataMapperService } from 'src/app/services/widget-metadata-mapper.service';
import { updateClinicalNoteNameRequested } from 'src/app/store/patient-clinical-note/patient-clinical-note.actions';
import { CombinedClinicalNoteAttachment } from 'src/app/store/patient-clinical-note/patient-clinical-note.selectors';
import { setDocumentInstanceWidgetValueRequested } from 'src/app/store/patient-task/patient-task.actions';
import { AppState } from 'src/app/store/reducers';
import { ClinicalNote, DocumentInstance } from 'src/app/types/aggregate-graph.types';
import { DocumentSectionExpandButtonsComponent } from '../../../document-section-expand-buttons/document-section-expand-buttons.component';
import { DocumentSectionComponent } from '../../../document-section/document-section.component';
import { AttachmentUploadComponent } from '../attachment-upload/attachment-upload.component';
import { CombinedAttachmentComponent } from '../combined-attachment/combined-attachment.component';

interface NameFormGroup {
	name: FormControl<string>;
}

@Component({
	selector: 'app-clinical-note-document',
	templateUrl: './clinical-note-document.component.html',
	styleUrl: './clinical-note-document.component.scss',
	imports: [
		AttachmentUploadComponent, CombinedAttachmentComponent, DocumentSectionExpandButtonsComponent, DocumentSectionComponent,
		HeadingComponent, ItemLabelComponent, ExpanderComponent, NgTemplateOutlet, ReactiveFormsModule, TextInputControl
	],
	providers: [ExpanderGroupService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [enter]
})
export class ClinicalNoteDocumentComponent implements OnChanges, OnDestroy {

	@Input() clinicalNote!: ClinicalNote;
	@Input() documentInstance!: DocumentInstance;
	@Input() combinedClinicalNoteAttachments: CombinedClinicalNoteAttachment[] = [];
	@Input() isReadOnly: boolean = true;

	viewModel!: DocumentViewModel;

	nameForm: FormGroup<NameFormGroup> | undefined;

	private lastChangedWidgetValue: WritableSignal<ChangedWidgetValue> = signal({ documentInstanceId: '', widgetId: 0, value: null });

	constructor(
		private documentInstanceBuildService: DocumentInstanceBuildService, private formBuilder: FormBuilder,
		private store: Store<AppState>, public widgetMetadataMapperService: WidgetMetadataMapperService,
		@Optional() private clinicalNoteFormService: ClinicalNoteFormService | null) {
		effect(() => {
			// effects always run at least once, so guard against saving the initial value
			const value = this.lastChangedWidgetValue();
			if (value.widgetId) this.store.dispatch(setDocumentInstanceWidgetValueRequested(value));
		});
	}

	private isBuilt: boolean = false;

	ngOnChanges(changes: SimpleChanges) {
		if (!this.isBuilt) {
			this.viewModel = this.documentInstanceBuildService.buildViewModel(this.documentInstance, this.isReadOnly, this.lastChangedWidgetValue);
			this.clinicalNoteFormService?.addForm(this.clinicalNote.id, this.viewModel.form);

			if (this.clinicalNote.manifest.nameChangeEnabled) {

				this.nameForm = this.formBuilder.group({
					// invalidate if name is falsy or matches the manifest name (ignoring case)
					name: this.formBuilder.nonNullable.control(this.clinicalNoteName, { validators: [Validators.required, Validators.pattern(new RegExp(`^(?!${this.clinicalNote.manifest.name}$).*$`, 'i'))] })
				});

				if (!this.isReadOnly) {
					this.nameForm.valueChanges.subscribe(value => {
						this.store.dispatch(updateClinicalNoteNameRequested({ clinicalNoteId: this.clinicalNote.id, name: value.name || this.clinicalNote.manifest.name }));
					});
				}
				this.clinicalNoteFormService?.addForm(this.clinicalNote.id, this.nameForm!);
			}

			this.isBuilt = true;
		}
		else if (this.isReadOnly) {
			if (changes['clinicalNote'] && this.nameForm) this.nameForm.setValue({ name: this.clinicalNoteName });
			if (changes['documentInstance']) this.documentInstanceBuildService.updateWidgets(this.documentInstance, this.viewModel);
		}
	}

	private get clinicalNoteName() {
		return this.clinicalNote.name.localeCompare(this.clinicalNote.manifest.name) ? this.clinicalNote.name : '';
	}

	get attachmentsLabel() {
		return this.clinicalNote.manifest.maximumAttachments! === 1 ? 'Attachment' : 'Attachments';
	}

	ngOnDestroy(): void {
		this.viewModel.subscriptionDestroyer.next();
		this.viewModel.subscriptionDestroyer.complete();
		this.clinicalNoteFormService?.deregister(this.clinicalNote.id);
	}
}